<template>
    <HorizontalPopover
        @open="popoverOpen = true"
        @close="onClose"
        componentId="notifications"
    >
        <template #reference>
            <div class="relative">
                <button
                    class="flex transform hover:scale-105 transition duration-300 p-2 items-center space-x-1 xs:whitespace-nowrap"
                >
                    <div
                        class="relative flex size-2.5"
                        v-if="hasUnreadNotifications"
                    >
                        <div
                            v-if="animateNotificationCircle"
                            class="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"
                        ></div>
                        <div
                            class="relative inline-flex rounded-full size-2.5 bg-sky-500"
                        ></div>
                    </div>
                    <p
                        v-text="$lang.get('My notifications')"
                        class="text-sm md:text-base"
                    />
                </button>
            </div>
        </template>
        <template #floating>
            <div
                v-if="popoverOpen"
                class="w-[88vw] xs:w-screen xs:max-w-sm md:max-w-md max-h-full text-text"
            >
                <div class="relative bg-white py-4 px-4 flex flex-col h-full">
                    <div id="notifications-menu" />
                    <div class="flex justify-between">
                        <p
                            class="mb-3 text-xl"
                            v-text="$lang.get('My notifications')"
                        />

                        <div class="flex gap-x-1">
                            <SecondaryButton size="xs" @click="openSettings">
                                <PhGear class="size-7" />
                            </SecondaryButton>

                            <component
                                @click="showMenu = !showMenu"
                                :is="showMenu ? 'PhX' : 'PhDotsThree'"
                                :class="[
                                    showMenu ? 'p-1' : 'px-1',
                                    'w-10 cursor-pointer hover:bg-gray-50 rounded-button transition-colors duration-button-hover',
                                ]"
                                :weight="showMenu ? 'light' : 'bold'"
                            />
                        </div>
                    </div>

                    <VerticalSlideTransition :show="showMenu">
                        <div class="w-full border-b">
                            <div
                                class="pt-3 pb-5 flex justify-center items-center gap-x-4"
                            >
                                <SecondaryButton
                                    size="sm"
                                    @click="toggleMarkAllAsRead"
                                    v-text="
                                        totalUnread == 0
                                            ? $lang.get('Mark all as unread')
                                            : $lang.get('Mark all as read')
                                    "
                                />
                                <DangerButton
                                    size="sm"
                                    @click="removeAllNotifications"
                                    v-text="
                                        $lang.get('Remove all notifications')
                                    "
                                />
                            </div>
                        </div>
                    </VerticalSlideTransition>
                    <div class="flex-grow overflow-hidden py-3 relative">
                        <div
                            ref="notificationList"
                            class="w-full max-h-full overflow-y-auto gap-y-4 flex flex-col pr-2"
                        >
                            <TransitionGroup
                                :name="
                                    listTransitionIsEnabled
                                        ? 'list-transition'
                                        : ''
                                "
                            >
                                <GroupedNotification
                                    v-for="group in groupedNotifications"
                                    :key="group.id"
                                    :group="group"
                                    :menuOpenId="menuOpenId"
                                    @toggleNotificationsAsRead="
                                        toggleNotificationsAsRead
                                    "
                                    @removeNotifications="removeNotifications"
                                    @closeMenu="menuOpenId = null"
                                    @openMenu="openMenu"
                                />
                            </TransitionGroup>
                            <Transition name="fade">
                                <div
                                    v-if="showNewNotificationPill"
                                    class="absolute top-5 left-1/2 -translate-x-1/2 z-10 flex justify-center"
                                >
                                    <PrimaryButton
                                        class="px-2 gap-x-1 whitespace-nowrap shadow-xl flex items-center"
                                        size="xs"
                                        @click="scrollToTop"
                                    >
                                        <PhArrowFatLinesUp class="size-4" />
                                        <p
                                            class="text-base"
                                            v-text="
                                                $lang.get(
                                                    'View your new notifications'
                                                )
                                            "
                                        />
                                        <PhArrowFatLinesUp class="size-4" />
                                    </PrimaryButton>
                                </div>
                            </Transition>
                        </div>
                        <p
                            v-if="notifications.length == 0"
                            v-text="$lang.get('You have no notifications')"
                            class="text-center text-lg font-medium"
                        />
                    </div>
                </div>
            </div>
        </template>
    </HorizontalPopover>

    <NotificationBadge
        v-if="showNotificationBadge"
        @close="showNotificationBadge = false"
        :notification="notificationBadge"
    />
    <NotificationSettingModal ref="notificationSetting" />
    <ConfirmDangerModal ref="dangerModal" />
</template>

<script>
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import HorizontalPopover from '@/Components/Utilities/HorizontalPopover.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import DangerButton from '@/Components/Elements/DangerButton.vue'
import SecondaryButton from '@/Components/Elements/SecondaryButton.vue'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import GroupedNotification from '@/Components/Notifications/GroupedNotification.vue'
import Notification from '@/Components/Notifications/Notification.vue'
import NotificationBadge from '@/Components/Notifications/NotificationBadge.vue'
import NotificationSettingModal from '@/Components/Notifications/NotificationSettingModal.vue'
import ConfirmDangerModal from '@/Components/Modals/ConfirmDangerModal.vue'

import { throttle, isElementInView } from '@/Tools/utilities'
import { Link, usePage } from '@inertiajs/vue3'
import {
    PhDotsThree,
    PhX,
    PhBell,
    PhGear,
    PhArrowFatLinesUp,
} from '@phosphor-icons/vue'
import { useInfiniteScroll } from '@vueuse/core'
import { nextTick, ref } from 'vue'

export default {
    emits: [
        'toggleNotificationsAsRead',
        'removeNotifications',
        'closeMenu',
        'openMenu',
        'closePopover',
    ],
    components: {
        ModelImage,
        Link,
        ReactiveHeightTransition,
        HorizontalPopover,
        VerticalSlideTransition,
        DangerButton,
        SecondaryButton,
        PrimaryButton,
        PhDotsThree,
        PhX,
        PhGear,
        PhBell,
        PhArrowFatLinesUp,
        GroupedNotification,
        NotificationBadge,
        Notification,
        NotificationSettingModal,
        ConfirmDangerModal,
    },

    props: {
        buttonClasses: {
            type: String,
        },
    },

    data() {
        return {
            page: 1,
            showMenu: false,
            popoverOpen: false,
            animateNotificationCircle: false,
            notificationBadge: false,
            showNewNotificationPill: false,
            showNotificationBadge: false,
            listTransitionIsEnabled: false,
            menuOpenId: null,
        }
    },

    setup() {
        const notificationList = ref(null)
        const allNotificationsRetrieved = ref(false)
        const page = ref(1)
        const notifications = ref([])
        const totalUnread = ref(0)

        totalUnread.value = usePage().props.auth.unread_notifications

        const fetchNotifications = async () => {
            await new Promise((resolve) => {
                axios
                    .get(route('api.notifications.index'), {
                        params: {
                            paginate: true,
                            page: page.value,
                        },
                    })
                    .then(({ data }) => {
                        page.value += 1
                        notifications.value.push(...data.notifications.data)
                        totalUnread.value = data.total_unread
                        if (data.notifications.data.length == 0) {
                            allNotificationsRetrieved.value = true
                        }
                        resolve()
                    })
            })
        }

        useInfiniteScroll(notificationList, fetchNotifications, {
            distance: 10,
            canLoadMore: () => allNotificationsRetrieved.value == false,
        })

        return {
            notificationList,
            fetchNotifications,
            notifications,
            totalUnread,
        }
    },

    computed: {
        groupedNotifications() {
            let group
            let group_type
            return this.notifications
                .reduce((groups, notification) => {
                    group_type =
                        notification.sourceable_type == 'interaction'
                            ? 'interaction'
                            : 'model_events'
                    group = groups.find(
                        (g) =>
                            g.id == notification.group.id &&
                            g.source_type == notification.group.source_type &&
                            g.type == group_type
                    )
                    if (!group) {
                        group = {
                            ...notification.group,
                            notifications: [],
                            type: group_type,
                            timestamp: null,
                        }
                        groups.push(group)
                    }
                    group.notifications.push(notification)

                    if (
                        group.timestamp == null ||
                        this.$moment
                            .utc(notification.created_at)
                            .isBefore(this.$moment(group.timestamp))
                    ) {
                        group.timestamp = notification.created_at
                    }

                    return groups
                }, [])
                .sort((a, b) => this.$moment(a).diff(this.$moment(b)))
        },

        hasUnreadNotifications() {
            return this.totalUnread > 0
        },

        notificationChannel() {
            return `App.Models.User.${this.$page.props.auth.user.id}`
        },
    },

    watch: {
        popoverOpen(newPopoverOpen) {
            if (newPopoverOpen) {
                this.initScrollListener()
            } else {
                this.destroyScrollListener()
            }
        },
    },

    created() {
        this.throttledScroll = throttle(
            (event) => {
                if (event?.currentTarget?.scrollTop == 0) {
                    this.showNewNotificationPill = false
                }
            },
            300,
            true
        )

        this.initEcho()
        this.fetchNotifications()
    },

    beforeUnmount() {
        this.destroyEcho()
        this.destroyScrollListener()
    },

    methods: {
        initEcho() {
            if (this.$page.props.auth.user) {
                this.echoChannel = window.Echo.private(this.notificationChannel)

                this.echoChannel.notification((notification) => {
                    this.enableListTransition()
                    this.fetchSingleNotification(notification.id).then(() => {
                        this.$nextTick(function () {
                            if (
                                this.$refs.notificationList &&
                                this.$refs.notificationList.scrollTop != 0
                            ) {
                                this.showNewNotificationPill = true
                            }
                        })
                        this.animateNewNotification({
                            notification_id: notification.id,
                            notification_level: notification.notification_level,
                        })
                    })
                })

                this.echoChannel.listen('NotificationReadEvent', (event) => {
                    if (event.notificationId) {
                        this.setNotificationAsRead(event.notificationId)
                    }
                })
            }
        },

        getFirstNotificationElement() {
            return this.$refs?.notificationList?.children?.length > 0
                ? this.$refs.notificationList.children[0]
                : null
        },

        getListElement() {
            return this.$refs?.notificationList
        },

        // on ref notificationList
        initScrollListener() {
            nextTick(() => {
                this.$refs.notificationList.addEventListener(
                    'scroll',
                    this.throttledScroll
                )
            })
        },

        destroyScrollListener() {
            if (this.$refs.notificationList) {
                this.$refs.notificationList.removeEventListener(
                    'scroll',
                    this.throttledScroll
                )
            }
        },

        scrollToTop() {
            const firstNotificationElement = this.getFirstNotificationElement()

            if (firstNotificationElement) {
                firstNotificationElement.scrollIntoView({ behavior: 'smooth' })
            }
        },

        destroyEcho() {
            if (this.$page.props?.auth?.user) {
                this.echoChannel = window.Echo.leaveChannel(
                    this.notificationChannel
                )

                this.echoChannel = null
            }
        },

        enableListTransition() {
            this.listTransitionIsEnabled = true

            setTimeout(() => {
                this.listTransitionIsEnabled = false
            }, 1000)
        },

        /**
         * Fetches a single notification
         * This is used when a notification is recieved via websockets
         */
        fetchSingleNotification(id, addToArray = true) {
            return new Promise((resolve, reject) => {
                axios
                    .get(route('api.notifications.show', id))
                    .then(({ data }) => {
                        if (addToArray) {
                            this.notifications.unshift(data.notification)
                        }
                        this.totalUnread = data.total_unread

                        resolve({
                            notification: data.notification,
                            parentRouteName: data.parent_route_name,
                        })
                    })
            })
        },

        /**
         * Fetches the number of unread notifications and updates it in the UI
         */
        fetchNumberNotifications() {
            axios
                .get(route('api.notifications.unread.count'))
                .then(({ data }) => {
                    this.totalUnread = data.total_unread
                })
                .catch(() => {
                    this.totalUnread = 0
                })
        },

        /**
         * Will set the given notifications as read or unread
         * If no notifications are given, it will set all notifications as read or unread
         *
         * @param read
         */
        toggleNotificationsAsRead({ notificationIds, read }) {
            axios
                .post(route('api.notifications.toggle.read'), {
                    mark_as_read: read,
                    notifications: notificationIds,
                })
                .then(({ data }) => {
                    this.notifications.forEach((notification) => {
                        if (
                            notificationIds.length == 0 ||
                            notificationIds.includes(notification.id)
                        ) {
                            notification.read_at = data.read_at
                        }
                    })
                    this.totalUnread = data.total_unread
                })
            this.menuOpenId = null
        },

        setNotificationAsRead(notificationId) {
            let newTotalUnread = 0

            this.notifications.forEach((notification) => {
                if (notification.id == notificationId) {
                    notification.read_at = new Date()
                } else if (notification.read_at == null) {
                    newTotalUnread++
                }
            })

            this.totalUnread = newTotalUnread
        },

        /**
         * Toggles all notifications as read or undread
         * depending on the totalUnread count
         */
        toggleMarkAllAsRead() {
            axios
                .post(route('api.notifications.toggle.allread'), {
                    mark_as_read: this.totalUnread > 0,
                })
                .then(({ data }) => {
                    this.notifications.forEach((notification) => {
                        notification.read_at = data.read_at
                    })
                    this.totalUnread = data.total_unread
                })
            this.showMenu = false
        },

        /**
         * Removes the given notifications
         */
        removeNotifications(notificationIds) {
            this.enableListTransition()
            axios
                .delete(
                    route(
                        'api.notifications.destroy',
                        notificationIds.join(',')
                    )
                )
                .then(({ data }) => {
                    this.notifications = this.notifications.filter(
                        (n) => !notificationIds.includes(n.id)
                    )
                    this.totalUnread = data.total_unread
                    this.totalNbrNotifications = data.total_notifications
                })
            this.menuOpenId = null
        },

        /**
         * Removes all the users notifications and closes the manu afterwards
         */
        removeAllNotifications() {
            this.$refs.dangerModal
                .confirm({
                    title: this.$lang.get('Delete :item', {
                        definitive_item_name:
                            this.$lang.get('all notifications'),
                    }),
                    body: this.$lang.get(
                        'Are you sure you want to delete :definitive_item_name?',
                        {
                            definitive_item_name: this.$lang.get(
                                'all the notifications'
                            ),
                        }
                    ),
                })
                .then((confirmed) => {
                    if (confirmed) {
                        this.enableListTransition()
                        axios
                            .delete(route('api.notifications.destroy.all'))
                            .then(({ data }) => {
                                this.notifications = []
                                this.totalUnread = data.total_unread
                                this.totalNbrNotifications =
                                    data.total_notifications
                            })
                    }
                    this.showMenu = false
                })
        },

        openMenu(notificationId) {
            this.menuOpenId = notificationId
            this.showMenu = false
        },

        /**
         * Opens the notification settings page
         */
        openSettings() {
            this.$refs.notificationSetting.open()
        },

        /**
         * Either animates the notification circle or shows a notification badge witht he new notification
         * Wich it does depenend on the notification level (Priority of the notification)
         *
         * @param notification_id
         * @param notification_level
         */
        animateNewNotification({ notification_id, notification_level }) {
            if ([1, 2, 3].includes(notification_level)) {
                this.fetchAndShowNotificationbadge(notification_id)
            } else {
                this.activeNotificationCircleAnimation()
            }
        },

        /**
         * Animates the notification circle to show that a new notification has arrived
         */
        activeNotificationCircleAnimation() {
            if (!this.animateNotificationCircle) {
                this.animateNotificationCircle = true
                setTimeout(() => {
                    this.animateNotificationCircle = false
                }, 3000)
            }
        },

        /**
         * Fetches the given notification and shows a notification badge with the notification
         *
         * @param notification_id
         */
        fetchAndShowNotificationbadge(notification_id) {
            this.fetchSingleNotification(notification_id, false).then(
                async ({ notification, parentRouteName }) => {
                    // If the parentRouteName is the same as the current Route, dont display a notificationBadge
                    if (route().current() == parentRouteName) {
                        this.activeNotificationCircleAnimation()
                    } else {
                        this.notificationBadge = notification
                        this.showNotificationBadge = true
                    }
                }
            )
        },

        onClose() {
            this.showNewNotificationPill = false
            this.popoverOpen = false
        },
    },
}
</script>
